import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { unpublishStore } from '@dukanti/shared';
import type { PublishStoreInput, PublishStoreResult } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

/**
 * Unpublish the merchant's store via the `publish_store(uuid, false)`
 * RPC. Mirror of {@link usePublishStore} for the inverse transition.
 *
 * On success the returned `Store` row is written directly into the
 * React Query cache so the dashboard re-renders synchronously (AC8).
 *
 * ## Children are NOT invalidated
 *
 * By design (F-004 Decision 10 step 4b / Decision 1) an unpublish
 * leaves child `products.published` values untouched so a subsequent
 * re-publish is clean. Invalidating `productKeys.list(storeId)` here
 * would trigger an unnecessary refetch against rows whose state has
 * not changed. If a future requirement needs the products list to
 * reflect anything about publish state, use a cache selector — don't
 * add an invalidate here.
 *
 * Errors are re-thrown so the calling dialog can map them itself.
 */
export function useUnpublishStore(): UseMutationResult<
  PublishStoreResult,
  unknown,
  PublishStoreInput
> {
  const queryClient = useQueryClient();

  return useMutation<PublishStoreResult, unknown, PublishStoreInput>({
    mutationFn: (input) => unpublishStore(supabase, input),
    onSuccess: (row, variables) => {
      // M1 contract: write the FULL row into the ['store', storeId] cache.
      // See `use-publish-store.ts` for the load-bearing cache shape note.
      queryClient.setQueryData(['store', variables.storeId], row);
    },
  });
}
